import { useIsCartInFlightRequests } from '@/features/pos/components/Cart/hooks/cart-optimistic-update-counter'
import { useAddCartContainerItem } from '@/features/pos/components/Cart/hooks/useAddCartContainerItem'
import ContainerModal from '@/features/pos/components/ProductConfigurationModal/ContainerModal'
import { usePortal } from '@/features/pos/components/ProductConfigurationModal/contexts/Portal'
import { maxProductQuantity } from '@/features/pos/components/ProductConfigurationModal/form/constants'
import {
    useConfigurableContainer,
    useConfigurableContainerActions,
} from '@/features/pos/contexts/ConfigurableContainer'
import { useCloseOnPosModeChange } from '@/features/pos/hooks/useCloseOnPosModeChange'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export const useConfigurableContainerModal = (): void => {
    const container = useConfigurableContainer()
    const { unset } = useConfigurableContainerActions()
    const isCartInFlightRequests = useIsCartInFlightRequests()
    const addContainer = useAddCartContainerItem()
    const [loading, setLoading] = useState(false)
    const [quantity, setQuantity] = useState(1)
    const inFlight = useRef(false)
    const confirmDisabled = isCartInFlightRequests

    useEffect(() => {
        setQuantity(1)
    }, [container?.id])

    const changeQuantity = useCallback((change: number) => {
        if (inFlight.current) return

        setQuantity((current) => Math.max(1, Math.min(maxProductQuantity, current + change)))
    }, [])

    const handleConfirm = useCallback(async () => {
        if (!container || inFlight.current || confirmDisabled) return

        inFlight.current = true
        setLoading(true)

        try {
            const added = await addContainer({ containerUuid: container.id, quantity })

            if (added) unset()
        } finally {
            inFlight.current = false
            setLoading(false)
        }
    }, [addContainer, confirmDisabled, container, quantity, unset])

    const handleCancel = useCallback(() => {
        if (!inFlight.current) unset()
    }, [unset])

    const modal = useMemo(
        () =>
            container && (
                <ContainerModal
                    name={container.name}
                    disabled={confirmDisabled}
                    loading={loading}
                    quantity={{
                        quantity,
                        disabled: loading,
                        decrease: () => changeQuantity(-1),
                        increase: () => changeQuantity(1),
                    }}
                    onCancel={handleCancel}
                    onConfirm={handleConfirm}
                />
            ),
        [changeQuantity, confirmDisabled, container, handleCancel, handleConfirm, loading, quantity]
    )

    usePortal(!!container, modal)
    useCloseOnPosModeChange(unset)
}
